/**
 * Chuẩn hoá payload realtime Gateway (`sensor_updated`, `water_level`, `rainfall`, …) → merge Redux `telemetryByStationId`.
 */
import { rainfallWindowCodeFromDurationH } from '@/features/realtime/utils/stationSensorBindings';

export interface IotTelemetryMergePayload {
  /** Lookup keys: station UUID and/or `code:${sensorCode}`. */
  keys: string[];
  value: number;
  timestamp?: number;
  /** UUID kênh catalog (`channel_id` / `channelId` từ WS). */
  channelId?: string;
  /** routing_key (`type` trên WS). */
  routingKey?: string;
  durationH?: number;
  rainfallWindowCode?: '1h' | '24h';
}

function num(v: unknown): number | undefined {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

/** Chuỗi ISO8601 hoặc epoch (giả định giây khi số < 1e12). */
function parseTimestampMs(o: Record<string, unknown>): number | undefined {
  const candidates = [o.timestamp, o.ts, o.time];
  for (const v of candidates) {
    if (typeof v === 'number' && Number.isFinite(v)) {
      return v > 1e12 ? Math.round(v) : Math.round(v * 1000);
    }
    if (typeof v === 'string' && v.trim()) {
      const n = Number(v);
      if (!Number.isNaN(n) && Number.isFinite(n)) {
        return n > 1e12 ? Math.round(n) : Math.round(n * 1000);
      }
      const t = Date.parse(v);
      if (Number.isFinite(t)) return t;
    }
  }
  return undefined;
}

function telemetryNumericValue(o: Record<string, unknown>): number | undefined {
  return (
    num(o.value) ??
    num(o.lastValue) ??
    num(o.reading) ??
    num(o.valueM) ??
    num(o.valueMm) ??
    num(o.value_m) ??
    num(o.value_mm) ??
    num(o.value_pct) ??
    num(o.value_c) ??
    num(o.value_g) ??
    num(o.kp) ??
    num(o.pressure_hpa)
  );
}

function routingKeyFromPayload(o: Record<string, unknown>): string | undefined {
  const t =
    (typeof o.type === 'string' && o.type.trim()) ||
    (typeof o.routing_key === 'string' && o.routing_key.trim()) ||
    '';
  return t || undefined;
}

function channelIdFromPayload(o: Record<string, unknown>): string | undefined {
  const id =
    (typeof o.channel_id === 'string' && o.channel_id.trim()) ||
    (typeof o.channelId === 'string' && o.channelId.trim()) ||
    '';
  return id || undefined;
}

/**
 * Parses one or more telemetry updates from a single socket message.
 * Supports: single object, array of objects, or nested `data` / `payload` wrapper.
 */
export function normalizeIotTelemetryPayload(raw: unknown): IotTelemetryMergePayload[] {
  if (raw == null) return [];

  if (Array.isArray(raw)) {
    return raw.flatMap((item) => normalizeIotTelemetryPayload(item));
  }

  if (typeof raw !== 'object') return [];

  const o = raw as Record<string, unknown>;
  const inner = o.data ?? o.payload;
  if (inner !== undefined && inner !== o) {
    const nested = normalizeIotTelemetryPayload(inner);
    if (nested.length > 0) return nested;
  }

  const stationId =
    (typeof o.stationId === 'string' && o.stationId.trim()) ||
    (typeof o.station_id === 'string' && o.station_id.trim()) ||
    (typeof o.id === 'string' && o.id.trim()) ||
    '';
  const code =
    (typeof o.code === 'string' && o.code) ||
    (typeof o.station_code === 'string' && o.station_code) ||
    (typeof o.sensorId === 'string' && o.sensorId) ||
    '';

  const value = telemetryNumericValue(o);
  if (value === undefined) return [];

  const timestamp = parseTimestampMs(o);
  const routingKey = routingKeyFromPayload(o);
  const channelId = channelIdFromPayload(o);
  const durationH = num(o.duration_h) ?? num(o.durationH);
  const rainfallWindowCode =
    routingKey === 'rainfall' ? rainfallWindowCodeFromDurationH(durationH) : undefined;

  const keys: string[] = [];
  if (stationId) keys.push(stationId);
  if (code) keys.push(`code:${code.trim()}`);
  if (keys.length === 0) return [];

  return [
    {
      keys,
      value,
      timestamp: timestamp ?? undefined,
      channelId,
      routingKey,
      durationH,
      rainfallWindowCode,
    },
  ];
}
